// client/src/components/contratos/AssinarContratoPublica.js
// Página pública (sem login) de assinatura remota de contrato — mesma
// máquina de estados de AssinarAnamnesePublica.js (por sua vez espelhada em
// ResetPasswordPage.js), com o mesmo passo de confirmação por código (OTP)
// antes de revelar o conteúdo — mitigação contra bots de preview de link
// (WhatsApp/Telegram buscam a URL automaticamente ao colar num chat).
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';
import apiClient from '../../services/api';
import AssinaturaCanvas from '../common/AssinaturaCanvas';
import useAlert from '../../hooks/useAlert';

const AssinarContratoPublica = () => {
    const { token } = useParams();
    const { setAlert } = useAlert();
    const canvasRef = useRef(null);

    // 'validating' | 'invalid' | 'valid' | 'code_sent' | 'confirmed' | 'signed'
    const [status, setStatus] = useState('validating');
    const [meta, setMeta] = useState(null);
    const [conteudo, setConteudo] = useState(null);
    const [codigo, setCodigo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setStatus('invalid');
            return;
        }
        apiClient.get(`/public/contratos/assinar/${token}`)
            .then((res) => {
                setMeta(res.data.data);
                setStatus('valid');
            })
            .catch(() => setStatus('invalid'));
    }, [token]);

    const handleEnviarCodigo = async () => {
        setIsSubmitting(true);
        try {
            await apiClient.post(`/public/contratos/assinar/${token}/enviar-codigo`);
            setAlert('Código de verificação enviado.', 'success');
            setStatus('code_sent');
        } catch (err) {
            setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar código.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmarCodigo = async (e) => {
        e.preventDefault();
        if (codigo.length !== 6) {
            setAlert('Informe o código de 6 dígitos recebido.', 'warning');
            return;
        }
        setIsSubmitting(true);
        try {
            const { data } = await apiClient.post(`/public/contratos/assinar/${token}/confirmar-codigo`, { codigo });
            setConteudo(data.data || null);
            setStatus('confirmed');
        } catch (err) {
            setAlert(err.response?.data?.error || err.response?.data?.message || 'Código inválido ou expirado.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAssinar = async () => {
        const assinatura = canvasRef.current?.getSignature();
        if (!assinatura) {
            setAlert('Assine no espaço indicado antes de confirmar.', 'warning');
            return;
        }
        setIsSubmitting(true);
        try {
            await apiClient.put(`/public/contratos/assinar/${token}`, { assinatura, codigo });
            setStatus('signed');
        } catch (err) {
            setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao assinar o contrato.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (status === 'validating') {
        return (
            <div className="auth-container">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <Loader2 className="animate-spin icon" size={32} />
                    <p style={{ marginTop: '1rem' }}>Validando link de assinatura...</p>
                </div>
            </div>
        );
    }

    if (status === 'invalid') {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <h1>Link <span>Inválido</span></h1>
                    <p>Este link de assinatura é inválido ou já expirou. Solicite um novo link ao estabelecimento.</p>
                    <div className="auth-links">
                        <p><Link to="/login">Voltar para o login</Link></p>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'signed') {
        return (
            <div className="auth-container">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <CheckCircle2 className="icon" size={48} style={{ color: 'var(--color-success, #22c55e)' }} />
                    <h1>Contrato <span>Assinado</span></h1>
                    <p>Sua assinatura foi registrada com sucesso. Obrigado!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-card" style={{ maxWidth: 640 }}>
                <h1>Assinatura de <span>Contrato</span></h1>
                <p>
                    {meta?.contraparteNome ? `Olá, ${meta.contraparteNome}. ` : ''}
                    Contrato Nº {meta?.numeroContrato} — {meta?.tipoContratoNome} — {meta?.accountNome}.
                </p>

                {status === 'valid' && (
                    <div className="form-actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <p className="text-muted">
                            Para revisar o contrato e assinar, vamos enviar um código de verificação por WhatsApp, SMS ou e-mail.
                        </p>
                        <button
                            type="button"
                            className="action-button action-button-primary w-full"
                            onClick={handleEnviarCodigo}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? <Loader2 className="animate-spin icon" /> : <ShieldCheck className="icon" />}
                            Enviar Código de Verificação
                        </button>
                    </div>
                )}

                {status === 'code_sent' && (
                    <form onSubmit={handleConfirmarCodigo}>
                        <div className="form-group">
                            <label htmlFor="codigo">Código de Verificação*</label>
                            <input
                                type="text"
                                id="codigo"
                                className="form-input"
                                value={codigo}
                                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                maxLength={6}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                disabled={isSubmitting}
                                autoFocus
                            />
                        </div>
                        <div className="form-actions">
                            <button type="submit" className="action-button action-button-primary w-full" disabled={isSubmitting}>
                                {isSubmitting ? <Loader2 className="animate-spin icon" /> : <ShieldCheck className="icon" />}
                                Confirmar Código
                            </button>
                        </div>
                        <div className="auth-links">
                            <p>
                                Não recebeu?{' '}
                                <button type="button" onClick={handleEnviarCodigo} disabled={isSubmitting} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', padding: 0 }}>
                                    Reenviar código
                                </button>
                            </p>
                        </div>
                    </form>
                )}

                {status === 'confirmed' && (
                    <div>
                        <div
                            style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', margin: '1rem 0', maxHeight: 420, overflowY: 'auto', background: '#fafafa' }}
                            dangerouslySetInnerHTML={{ __html: conteudo?.conteudoHtml || '<p>(conteúdo não disponível)</p>' }}
                        />

                        <div className="form-group full-width">
                            <label className="form-label">Assinatura*</label>
                            <AssinaturaCanvas ref={canvasRef} />
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="action-button action-button-primary w-full"
                                onClick={handleAssinar}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Loader2 className="animate-spin icon" /> : <CheckCircle2 className="icon" />}
                                Confirmar Assinatura
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssinarContratoPublica;
